import { notify } from "@portal/BaseComponents/Notifications";
import {
  checkCustomerLevelPTPAmount,
  deletePtp,
  getPTPDetails,
  getPTPList,
  raiseAPromiseToPay,
  raiseAPromiseToPayBulk,
  raiseCustomerLevelPromiseToPay,
  updatePTPStatus,
} from "@portal/api/ptp-list";
import { AppPageParams } from "@portal/lib/router/constants/page-info";
import { RequestConfig } from "@portal/types/common";

import { tableAtom } from "@portal/store/table/table";
import {
  CreateCustomerLevelPTP,
  CreatePTP,
  PTPDetails,
} from "@portal/types/entities/ptp";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import produce from "immer";
import { useSetAtom } from "jotai";
import { useParams } from "react-router-dom";
import { portalTabQueryKeys } from "@portal/Components/Portal/PotalTabs/types";

interface CreatePTPparams {
  onSuccessCallBack?: () => void;
  getUpdatedValues?: (values: CreatePTP) => void;
}
function usePTPListQuery(params: RequestConfig<unknown>) {
  const setTableValue = useSetAtom(tableAtom);

  return useQuery({
    queryKey: [portalTabQueryKeys.PTPs, params],
    queryFn: () => getPTPList(params),
    retry: false,
    async onSuccess(data) {
      setTableValue((value) => {
        return produce(value, (draft) => {
          draft.bulk_records = data.total_records;
          return draft;
        });
      });
    },
    onError(err) {},
  });
}

function useGetPTPDetailsQuery(params?: string) {
  return useQuery({
    queryKey: ["ptp-details", params],
    queryFn: () => getPTPDetails(params),
    retry: false,
    enabled: Boolean(params?.length),
  });
}

function useCreatePTPQuery(props: CreatePTPparams) {
  const { onSuccessCallBack, getUpdatedValues } = props;
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-ptp"],
    mutationFn: (data: CreatePTP) => raiseAPromiseToPay(data),
    onSuccess(data) {
      notify.success("PTP Created Successfully");
      onSuccessCallBack?.();
      getUpdatedValues?.(data);
      queryClient.invalidateQueries([portalTabQueryKeys.PTPs]);
    },
    onError() {
      notify.error("Failed to create PTP. Please try again later.");
    },
  });
}
function useCreateCustomerLevelPTPQuery(props: { onSuccessCallBack?: () => void }) {
  const { onSuccessCallBack } = props;
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-customer-level-ptp"],
    mutationFn: (data: CreateCustomerLevelPTP) => raiseCustomerLevelPromiseToPay(data),
    onSuccess() {
      notify.success("PTP Created Successfully");
      onSuccessCallBack?.();
      queryClient.invalidateQueries([portalTabQueryKeys.PTPs]);
    },
    onError() {
      notify.error("Failed to create PTP. Please try again later.");
    },
  });
}

/**
 * The room left for a customer-level promise, asked with no amount so the form can show it
 * before anything is typed. Also the source of the customer's currency for the amount input.
 */
function useCustomerLevelPTPRoomQuery(params: { enabled: boolean; payDate?: string }) {
  const { enabled, payDate } = params;
  return useQuery({
    queryKey: ["customer-level-ptp-room", payDate],
    queryFn: () => checkCustomerLevelPTPAmount({ pay_date: payDate }),
    enabled,
    retry: false,
  });
}

function useCreateBulkPTPQuery(props: CreatePTPparams) {
  const { onSuccessCallBack } = props;
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-bulk-ptp"],
    mutationFn: (data: CreatePTP) => raiseAPromiseToPayBulk(data),
    onSuccess(data) {
      notify.success("PTP Created Successfully");
      onSuccessCallBack?.();
      queryClient.invalidateQueries([portalTabQueryKeys.PTPs]);
    },
    onError() {
      notify.error("Failed to create PTP. Please try again later.");
    },
  });
}

function useDeletePTPQuery(props: { onClose?: () => void }) {
  const { ptp_id } = useParams<AppPageParams>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-ptp"],
    mutationFn: () => deletePtp(ptp_id),
    onSuccess() {
      notify.success("PTP Deleted");
      props.onClose?.();
      queryClient.invalidateQueries([portalTabQueryKeys.PTPs]);
    },
    onError() {
      notify.error("Failed to delete PTP. Please try again later.");
    },
  });
}
function useUpdatePTPStatusQuery() {
  const { ptp_id } = useParams<AppPageParams>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-ptp-status"],
    mutationFn: (data: Partial<CreatePTP>) => updatePTPStatus(data, ptp_id),
    onSuccess(data) {
      const currentData = queryClient.getQueriesData<PTPDetails>([
        "ptp-details",
        ptp_id,
      ]);
      queryClient.invalidateQueries([portalTabQueryKeys.PTPs]);

      if (!currentData.length) return;
      const [queryKey] = currentData[0];

      queryClient.setQueryData(queryKey, data);

      notify.success("PTP status updated successfully");
    },
    onError() {
      notify.error("Failed to update PTP status. Please try again later.");
    },
  });
}

export {
  useCreateBulkPTPQuery,
  useCreateCustomerLevelPTPQuery,
  useCustomerLevelPTPRoomQuery,
  useCreatePTPQuery,
  useDeletePTPQuery,
  useGetPTPDetailsQuery,
  usePTPListQuery,
  useUpdatePTPStatusQuery,
};
